import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DateTime, Interval } from 'luxon';
import { LocalDateTimeRangeDTO } from 'src/app/services/api/api';
import { SbLocalDateTime } from 'src/app/services/api/api-date-time';
import { UtilService } from 'src/app/services/util.service';
import { UserService } from '../../../services/user.service';

@Component({
  selector: 'sb-calendar-month',
  templateUrl: './sb-calendar-month.component.html',
  styleUrls: ['./sb-calendar-month.component.scss'],
  standalone: false,
})
export class SbCalendarMonthComponent implements OnInit {
  currentMonth: number;
  currentYear: number;
  currentDisplayRange: { from: SbLocalDateTime; till: SbLocalDateTime };

  currentDay: SbLocalDateTime;

  @Output()
  chosenMonthChanged: EventEmitter<any> = new EventEmitter();

  // tslint:disable-next-line:variable-name
  _dateTimeRanges: LocalDateTimeRangeDTO[];
  @Input()
  set dateTimeRanges(value: LocalDateTimeRangeDTO[]) {
    if (value) {
      this._dateTimeRanges = value;
    } else {
      this._dateTimeRanges = [];
    }

    try {
      this.render();
    } catch (e) {}
  }

  get dateTimeRanges(): LocalDateTimeRangeDTO[] {
    return this._dateTimeRanges;
  }

  currentMode: string = 'MONTH';

  // weekdays: string[] = [
  //   "MON",
  //   "TUE",
  //   "WED",
  //   "THU",
  //   "FRI",
  //   "SAT",
  //   "SUN"
  // ];

  weekdays: string[] = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  rows: any[][] = [];

  constructor(
    public readonly util: UtilService,
    public readonly userSettings: UserService
  ) {}

  ngOnInit() {
    if (this.userSettings.selectedLanguage() === 'de') {
      this.weekdays = ['M', 'D', 'M', 'D', 'F', 'S', 'S'];
    }
    const now: DateTime = DateTime.local();
    this.setMonth(now.month, now.year);
    this.render();
  }

  setCurrentDay(col: any) {
    this.currentDay = col.moment;
    this.currentMode = 'DAY';
  }

  incMonth() {
    if (this.currentMonth === 12) {
      this.currentMonth = 1;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }

    this.setMonth(this.currentMonth, this.currentYear);
    this.render();
  }

  decMonth() {
    if (this.currentMonth === 1) {
      this.currentMonth = 12;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }

    this.setMonth(this.currentMonth, this.currentYear);
    this.render();
  }

  render() {
    this.currentDisplayRange = this.getDisplayRangeForMonth(this.currentMonth, this.currentYear);
    this.rows = [];

    const startDate: SbLocalDateTime = this.currentDisplayRange.from;
    const endDate: SbLocalDateTime = this.currentDisplayRange.till;

    let tempMo: SbLocalDateTime = new SbLocalDateTime(startDate.value);
    let counter: number = 0;
    let row: any[] = [];

    while (tempMo.value <= endDate.value) {
      if (counter === 0) {
        if (row.length > 0) {
          this.rows.push(row);
          row = [];
        }
      }

      const mom: SbLocalDateTime = new SbLocalDateTime(tempMo.value.plus({ days: 1 }));

      const isMarked: boolean = this.checkMarked(tempMo);

      row.push({
        day: tempMo.value.day,
        moment: tempMo,
        isDayOfMonth: tempMo.value.month === this.currentMonth,
        isMarked,
        isPartial: isMarked ? false : this.checkPartial(tempMo),
      });
      tempMo = mom;

      counter++;

      if (counter === 7) {
        counter = 0;
      }
    }

    if (counter === 0) {
      if (row.length > 0) {
        this.rows.push(row);
      }
    }
  }

  goBack() {
    this.currentMode = 'MONTH';
  }

  getTitle() {
    switch (this.currentMode) {
      case 'MONTH':
        return this.getCurrentMonthName();
      case 'DAY':
        return this.getCurrentDayName();
    }
  }

  getCurrentMonthName() {
    return DateTime.local()
      .set({
        year: this.currentYear,
        month: this.currentMonth,
      })
      .toFormat('MMMM yyyy', { locale: this.userSettings.selectedLanguage() });
  }

  getCurrentDayName() {
    if (this.currentDay) {
      return this.currentDay.value.toFormat('EEEE, d. MMMM yyyy', { locale: this.userSettings.selectedLanguage() });
    }
  }

  setMonth(month: number, year: number) {
    this.currentMonth = month;
    this.currentYear = year;

    this.chosenMonthChanged.emit({ month: this.currentMonth, year: this.currentYear });
  }

  checkMarked(tempMo: SbLocalDateTime) {
    if (this._dateTimeRanges == null || this._dateTimeRanges.length === 0) {
      return false;
    } else {
      const startOfDay = tempMo.value.startOf('day');
      const endOfDay = tempMo.value.endOf('day');
      for (const range of this._dateTimeRanges) {
        const moFrom: SbLocalDateTime = new SbLocalDateTime(range.from.value);
        const moTill: SbLocalDateTime = new SbLocalDateTime(range.till.value);

        if (!moTill.encodeMillis) {
          // add milliseconds to day
          moTill.value = moTill.value.set({ millisecond: 999 });
        }

        const startOfDayMatches: boolean = startOfDay >= moFrom.value;
        const endOfDayMatches: boolean = endOfDay <= moTill.value;
        const startOfDayBetween: boolean = Interval.fromDateTimes(moFrom.value, moTill.value).contains(startOfDay);
        const endOfDayBetween: boolean = Interval.fromDateTimes(moFrom.value, moTill.value).contains(endOfDay);

        if ((startOfDayMatches || startOfDayBetween) && (endOfDayMatches || endOfDayBetween)) {
          return true;
        }
      }

      return false;
    }
  }

  checkPartial(tempMo: SbLocalDateTime) {
    if (this._dateTimeRanges == null || this._dateTimeRanges.length === 0) {
      return false;
    } else {
      for (const range of this._dateTimeRanges) {
        const moFrom = range.from.value.startOf('day');
        const moTill = range.till.value.endOf('day');

        if (tempMo.value === moFrom || tempMo.value === moTill || Interval.fromDateTimes(moFrom, moTill).contains(tempMo.value)) {
          return true;
        }
      }
    }
    return false;
  }

  getClasses(col) {
    if (col.isDayOfMonth) {
      let clazz: string = '';
      clazz += 'day-of-current-month cursor-pointer';
      if (col.isPartial) {
        clazz += ' partial';
      } else if (col.isMarked) {
        clazz += ' marked';
      }
      return clazz;
    } else {
      return 'not-day-of-current-month';
    }
  }

  getDisplayRangeForMonth(month: number, year: number): { from: SbLocalDateTime; till: SbLocalDateTime } {
    const mo: DateTime = DateTime.local().set({ year, month }).startOf('month');
    // mo.isoWeekday //1 = monday; 7 = sunday
    let tempMo: DateTime = mo;
    while (tempMo.weekday !== 1) {
      tempMo = tempMo.minus({ days: 1 });
    }
    const startDate: DateTime = tempMo;

    tempMo = mo.endOf('month');

    while (tempMo.weekday !== 7) {
      tempMo = tempMo.plus({ days: 1 });
    }

    let endDate: DateTime = tempMo;

    const diff = startDate.diff(endDate, 'days');

    if (diff.days !== -41) {
      tempMo = tempMo.plus({ days: 1 });
      while (tempMo.weekday !== 7) {
        tempMo = tempMo.plus({ days: 1 });
      }
      endDate = tempMo;
    }

    return {
      from: new SbLocalDateTime(startDate),
      till: new SbLocalDateTime(endDate),
    };
  }
}
